import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtTL, ExpiryBadge, StockBadge } from "@/components/StatusBadge";
import { productImage } from "@/lib/images";
import { AiInsightsCard, ReorderCard } from "@/components/DashboardAi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Package, TrendingUp, Wallet, AlertTriangle, Clock, Boxes, ShoppingCart, Sparkles,
  TrendingDown, DoorOpen, DoorClosed, Percent, Receipt, Banknote, CreditCard, MapPin, Plug, Settings2, RefreshCw, History, Award, ChevronDown, Activity, SlidersHorizontal, CheckCircle2, Users, CalendarDays, Share2, Send, Tag, Save, AlertOctagon, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { WIDGET_CATALOG, resolveVisibleWidgets, normalizeConfig, DEFAULT_DASHBOARD_CONFIG, defaultPresetFor } from "@/lib/dashboardWidgets";

const SPAN_CLS = { 1: "col-span-1", 2: "col-span-2", 4: "col-span-2 lg:col-span-4" };

function SalesRankCard({ testid, title, icon: Icon, accent, rows, metric, nav, cardCls }) {
  return (
    <div className={cardCls} data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${accent}`} />
        <p className="font-heading text-sm font-bold text-slate-800">{title}</p>
      </div>
      <div className="space-y-1">
        {(!rows || rows.length === 0) && <p className="text-sm text-slate-400">Son 30 günde veri yok.</p>}
        {(rows || []).slice(0, 6).map((p, i) => (
          <div key={p.id} data-testid={`${testid}-row-${p.id}`} onClick={() => nav("/urunler")}
            className="flex cursor-pointer items-center gap-2.5 rounded-md p-1.5 transition-colors hover:bg-slate-50">
            <span className="w-4 shrink-0 text-center text-[11px] font-bold text-slate-400">{i + 1}</span>
            <img src={productImage(p)} alt="" className="h-8 w-8 shrink-0 rounded-md object-cover" />
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{p.name}</p>
            <span className="tabular shrink-0 text-xs font-bold text-slate-700">
              {metric === "profit" ? fmtTL(p.profit) : `${p.qty} ${p.unit || "ad"}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopBrandsCard({ cardCls, nav }) {
  const [channel, setChannel] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const end = new Date();
    const start = new Date(); start.setDate(start.getDate() - 30);
    const qs = `start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}${channel ? `&channel=${channel}` : ""}`;
    api.get(`/reports/brand-summary?${qs}`)
      .then((r) => { if (!alive) return; const bs = (r.data?.brands || []).slice().sort((a, b) => (b.profit ?? 0) - (a.profit ?? 0)).slice(0, 5); setRows(bs); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [channel]);
  const CH = [["", "Tümü"], ["pos", "Perakende"], ["wholesale", "Toptan"]];
  return (
    <div className={`${cardCls} h-full`} data-testid="dash-top-brands">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Tag className="h-4 w-4 text-brand-green" />
        <p className="font-heading text-sm font-bold text-slate-800">En Kârlı 5 Marka (30 gün)</p>
        <button data-testid="dash-top-brands-more" onClick={() => nav("/raporlar")} className="ml-auto text-[11px] font-semibold text-brand-green hover:underline">Raporlar →</button>
      </div>
      <div className="mb-2 inline-flex rounded-md bg-slate-100 p-0.5" data-testid="dash-top-brands-channels">
        {CH.map(([k, l]) => (
          <button key={k || "all"} data-testid={`dash-top-brands-ch-${k || "all"}`} onClick={() => setChannel(k)}
            className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${channel === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="py-4 text-center text-sm text-slate-400" data-testid="dash-top-brands-loading">Yükleniyor…</p>
      ) : !rows || rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400" data-testid="dash-top-brands-empty">Bu kanalda son 30 günde marka verisi yok.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((b, i) => (
            <div key={b.brand} data-testid={`dash-top-brand-row-${i}`} onClick={() => nav("/raporlar")}
              className="flex cursor-pointer items-center gap-2.5 rounded-md p-1.5 transition-colors hover:bg-slate-50">
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${i < 3 ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{b.brand}</p>
              <div className="shrink-0 text-right">
                <p className="tabular text-sm font-bold text-emerald-700">{fmtTL(b.profit)}</p>
                <p className="text-[10px] text-slate-400" title="Önceki 30 güne göre ciro değişimi">
                  ciro {fmtTL(b.revenue)} · {b.trend_pct == null
                    ? <span className="font-semibold text-sky-600">Yeni</span>
                    : b.trend_pct > 0 ? <span className="font-semibold text-emerald-600">▲ %{b.trend_pct}</span>
                      : b.trend_pct < 0 ? <span className="font-semibold text-red-600">▼ %{Math.abs(b.trend_pct)}</span>
                        : <span className="text-slate-400">%0</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RevenueChannelsCard({ d, cardCls }) {
  const canProfit = !!(d.can_purchase && d.profit_today_channels);
  const [mode, setMode] = useState("revenue");
  const isProfit = mode === "profit" && canProfit;
  const eff = isProfit ? (d.profit_today_channels || {}) : (d.revenue_today_channels || {});
  const total = isProfit ? (d.profit_today || 0) : (d.revenue_today || 0);
  const rows = [
    { key: "pos", label: "Perakende", tone: "text-emerald-700", bg: "bg-emerald-500" },
    { key: "wholesale", label: "Toptan", tone: "text-sky-700", bg: "bg-sky-500" },
  ];
  if ((eff.eticaret || 0) !== 0) rows.push({ key: "eticaret", label: "E-Ticaret", tone: "text-violet-700", bg: "bg-violet-500" });
  if ((eff.other || 0) !== 0) rows.push({ key: "other", label: "Diğer", tone: "text-slate-700", bg: "bg-slate-400" });
  return (
    <div className={`${cardCls} h-full`} data-testid="dash-revenue-channels">
      <div className="mb-3 flex items-center gap-2">
        <Wallet className="h-4 w-4 text-brand-green" />
        <p className="font-heading text-sm font-bold text-slate-800">Günlük {isProfit ? "Kâr" : "Ciro"} — Kanal Kırılımı</p>
        <span className="ml-auto tabular text-sm font-bold text-slate-900" data-testid="dash-revch-total">{fmtTL(total)}</span>
      </div>
      {canProfit && (
        <div className="mb-2 inline-flex rounded-md bg-slate-100 p-0.5" data-testid="dash-revch-mode">
          {[["revenue", "Ciro"], ["profit", "Kâr"]].map(([k, l]) => (
            <button key={k} data-testid={`dash-revch-mode-${k}`} onClick={() => setMode(k)}
              className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${mode === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>
      )}
      {total <= 0 ? (
        <p className="py-4 text-center text-sm text-slate-400" data-testid="dash-revch-empty">Bugün henüz {isProfit ? "kâr" : "satış"} yok.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => {
            const val = eff[r.key] || 0;
            const pct = total !== 0 ? Math.round((val / total) * 100) : 0;
            const barW = Math.max(0, Math.min(100, pct));
            return (
              <div key={r.key} data-testid={`dash-revch-${r.key}`}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-slate-600">{r.label}</span>
                  <span className={`tabular font-semibold ${r.tone}`}>{fmtTL(val)} <span className="text-slate-400">· %{pct}</span></span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full ${r.bg} transition-all`} style={{ width: `${barW}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, activeSector, can } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [health, setHealth] = useState(null);
  const [ch, setCh] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [hist, setHist] = useState({ open: false, key: null, label: null, items: null, loading: false });
  const [expAlert, setExpAlert] = useState(null);
  const [showAllExp, setShowAllExp] = useState(false);
  const [showAllLow, setShowAllLow] = useState(false);
  const [cfg, setCfg] = useState(DEFAULT_DASHBOARD_CONFIG);
  const [hourDetail, setHourDetail] = useState({ open: false, date: null, hour: null, loading: false, data: null });
  const [shiftReco, setShiftReco] = useState(null);
  const [vehDocs, setVehDocs] = useState([]);
  const [dueSum, setDueSum] = useState(null);
  const [docExp, setDocExp] = useState(null);
  const [docComp, setDocComp] = useState(null);
  const [aiImg, setAiImg] = useState(null);
  const [aiImgBusy, setAiImgBusy] = useState(false);
  const [custHist, setCustHist] = useState({ open: false, loading: false, name: null, data: null });
  const [smsBox, setSmsBox] = useState({ text: "", sending: false });
  const [shiftPlan, setShiftPlan] = useState({ open: false, loading: false, saving: false, sharing: false, weekStart: null, personnel: [], days: [] });

  const loadCfg = useCallback(() => api.get("/settings/dashboard-config").then((r) => { const raw = r.data; setCfg(raw && raw.preset ? normalizeConfig(raw) : normalizeConfig({ preset: defaultPresetFor(user?.role, activeSector) })); }).catch(() => {}), [activeSector, user?.role]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setD(r.data)).catch(() => {});
    api.get("/inventory/expiry-alert").then((r) => setExpAlert(r.data)).catch(() => {});
    api.get("/analytics/catalog-health").then((r) => setCh(r.data)).catch(() => {});
    api.get("/dashboard/shift-recommendation").then((r) => setShiftReco(r.data)).catch(() => {});
    if (can("logistics.read")) api.get("/vehicles").then((r) => setVehDocs((r.data || []).filter((v) => (v.doc_alerts || []).length > 0))).catch(() => {});
    if (can("accounting.read")) api.get("/accounting/action-summary").then((r) => setDueSum(r.data)).catch(() => {});
    if (can("dashboard.read")) api.get("/documents/expiring?days=30").then((r) => setDocExp(r.data)).catch(() => {});
    if (can("dashboard.read")) api.get("/documents/compliance").then((r) => setDocComp(r.data)).catch(() => {});
    loadCfg();
    const loadHealth = () => api.get("/integrations/health").then((r) => setHealth(r.data)).catch(() => {});
    loadHealth();
    const t = setInterval(loadHealth, 60000);
    return () => clearInterval(t);
  }, [can, loadCfg]);

  const openIntegration = (key) => nav(key === "edoc" ? "/e-belge" : "/entegrasyonlar");
  const syncIntegration = async (key) => {
    const url = key === "ticimax" ? "/integrations/ticimax/sync-stock" : key === "trendyol" ? "/integrations/trendyol/sync-stock" : null;
    if (!url) return;
    setSyncing(key);
    try {
      const { data } = await api.post(url);
      toast.success(`${data.sent ?? 0} ürün gönderildi${data.skipped ? ` · ${data.skipped} atlandı` : ""}`);
      api.get("/integrations/health").then((r) => setHealth(r.data)).catch(() => {});
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Senkron başarısız");
    } finally {
      setSyncing(null);
    }
  };
  const openHistory = async (h) => {
    setHist({ open: true, key: h.key, label: h.label, items: null, loading: true });
    try {
      const { data } = await api.get(`/integrations/${h.key}/sync-history`);
      setHist((s) => ({ ...s, items: data, loading: false }));
    } catch {
      setHist((s) => ({ ...s, items: [], loading: false }));
    }
  };
  const openHourDetail = async (date, hour) => {
    setHourDetail({ open: true, date, hour, loading: true, data: null });
    try {
      const { data } = await api.get(`/dashboard/hourly-detail?date=${date}&hour=${hour}`);
      setHourDetail({ open: true, date, hour, loading: false, data });
    } catch {
      setHourDetail({ open: true, date, hour, loading: false, data: { items: [], count: 0, total: 0 } });
    }
  };
  const openCustomerHistory = async (cid, name) => {
    if (!cid) return;
    setSmsBox({ text: "", sending: false });
    setCustHist({ open: true, loading: true, name, data: null });
    try {
      const { data } = await api.get(`/customers/${cid}/statement`);
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
      const sales30 = (data.sales || []).filter((s) => { try { return new Date(s.created_at) >= cutoff; } catch { return false; } });
      const total30 = sales30.reduce((a, s) => a + (s.total || 0), 0);
      setCustHist({ open: true, loading: false, name, data: { ...data, sales30, total30 } });
    } catch {
      setCustHist({ open: true, loading: false, name, data: null });
    }
  };
  const loadAiImgStatus = () => api.get("/products/image-gen/status").then((r) => setAiImg(r.data)).catch(() => {});
  const triggerAiImages = async () => {
    setAiImgBusy(true);
    try {
      const { data } = await api.post("/products/generate-missing-images", { limit: 50, auto_continue: true });
      toast.success(data.message || "AI görsel üretimi başladı");
      loadAiImgStatus();
      const t = setInterval(async () => {
        try {
          const { data: st } = await api.get("/products/image-gen/status");
          setAiImg(st);
          if (!st.running) clearInterval(t);
        } catch { clearInterval(t); }
      }, 3000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Görsel üretimi başlatılamadı");
    } finally {
      setAiImgBusy(false);
    }
  };
  useEffect(() => { if (d?.is_admin) loadAiImgStatus(); }, [d?.is_admin]);
  const loadSchedule = async (weekStart) => {
    setShiftPlan((s) => ({ ...s, open: true, loading: true }));
    try {
      const q = weekStart ? `?week=${weekStart}` : "";
      const { data } = await api.get(`/shift-schedule${q}`);
      setShiftPlan((s) => ({ ...s, open: true, loading: false, weekStart: data.week_start, personnel: data.personnel || [],
        days: (data.days || []).map((d) => ({ ...d, assigned_ids: d.assigned_ids || [], note: d.note || "" })) }));
    } catch {
      setShiftPlan((s) => ({ ...s, open: true, loading: false, days: [], personnel: [] }));
    }
  };
  const openShiftSchedule = () => loadSchedule(null);
  const changeWeek = (delta) => {
    const base = new Date(shiftPlan.weekStart);
    base.setDate(base.getDate() + delta * 7);
    loadSchedule(base.toISOString().slice(0, 10));
  };
  const toggleAssign = (di, pid) => setShiftPlan((s) => ({
    ...s, days: s.days.map((d, i) => i !== di ? d : ({ ...d, assigned_ids: d.assigned_ids.includes(pid) ? d.assigned_ids.filter((x) => x !== pid) : [...d.assigned_ids, pid] })),
  }));
  const setDayNote = (di, note) => setShiftPlan((s) => ({ ...s, days: s.days.map((d, i) => i === di ? { ...d, note } : d) }));
  const persistSchedule = () => api.put("/shift-schedule", { week_start: shiftPlan.weekStart, days: shiftPlan.days.map((d) => ({ date: d.date, assigned: d.assigned_ids, note: d.note })) });
  const saveSchedule = async () => {
    setShiftPlan((s) => ({ ...s, saving: true }));
    try { await persistSchedule(); toast.success("Vardiya çizelgesi kaydedildi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setShiftPlan((s) => ({ ...s, saving: false })); }
  };
  const shareSchedule = async () => {
    setShiftPlan((s) => ({ ...s, sharing: true }));
    try {
      await persistSchedule();
      const { data } = await api.post("/shift-schedule/share", { week_start: shiftPlan.weekStart });
      toast.success(data.message || "Ekiple paylaşıldı");
    } catch (e) { toast.error(e.response?.data?.detail || "Paylaşılamadı"); }
    finally { setShiftPlan((s) => ({ ...s, sharing: false })); }
  };
  const applyDiscountTemplate = () => setSmsBox((s) => ({ ...s, text: "Groot Market: Size ozel %10 indirim firsati! Bu haftaya ozel magazamizda gecerlidir. Bekleriz." }));
  const sendCustomerSms = async (cid, name) => {
    if (!cid) return;
    const message = smsBox.text.trim();
    if (message.length < 3) { toast.error("Mesaj en az 3 karakter olmalı"); return; }
    setSmsBox((s) => ({ ...s, sending: true }));
    try {
      const { data } = await api.post("/sms/campaign", { audience: "selected", customer_ids: [cid], message });
      if (data.mocked) toast.success(`SMS hazırlandı (demo — Twilio yapılandırılmadı)${name ? " · " + name : ""}`);
      else toast.success(`SMS gönderildi: ${data.sent} başarılı, ${data.skipped} atlandı`);
      setSmsBox({ text: "", sending: false });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "SMS gönderilemedi (yetki gerekebilir)");
      setSmsBox((s) => ({ ...s, sending: false }));
    }
  };

  if (!d) return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-slate-200/60" />)}
    </div>
  );

  /* ---------- Kasiyer paneli (report.read yoksa) ---------- */
  if (!d.can_report) {
    const s = d.my_shift;
    return (
      <div className="space-y-6 fade-in">
        <div>
          <p className="overline">Groot Market · Kasiyer Paneli</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Merhaba {user?.name?.split(" ")[0]}</h1>
        </div>

        {!s ? (
          <div className="mx-auto max-w-md rounded-2xl bg-white p-8 text-center ring-1 ring-inset ring-slate-200 shadow-sm" data-testid="cashier-no-shift">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100"><DoorClosed className="h-7 w-7 text-slate-400" /></div>
            <h2 className="font-heading text-lg font-bold text-slate-800">Kasanız Kapalı</h2>
            <p className="mt-1 text-sm text-slate-500">Vardiyaya başlamak için kasayı açın.</p>
            <Button data-testid="go-open-shift" onClick={() => nav("/kasa")} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">
              <DoorOpen className="mr-2 h-4 w-4" /> Kasaya Git
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-xl bg-slate-900 p-3 text-sm text-white" data-testid="cashier-shift-bar">
              <DoorOpen className="h-4 w-4 text-emerald-400" /> Kasa Açık
              <span className="text-slate-300">· Başlangıç {s.opened_at ? new Date(s.opened_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
              <Button data-testid="go-pos" onClick={() => nav("/kasa")} size="sm" className="ml-auto bg-brand-orange text-white hover:bg-orange-600">Satışa Devam</Button>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Kpi icon={Wallet} label="Açılış Tutarı" value={fmtTL(s.opening_amount)} sub="devir" accent="bg-slate-700" />
              <Kpi icon={Receipt} label="Toplam Satış" value={fmtTL(s.grand_total)} sub={`${s.sales_count} satış`} accent="bg-brand-green" />
              <Kpi icon={Banknote} label="Nakit" value={fmtTL(s.cash_total)} sub="tahsilat" accent="bg-emerald-600" />
              <Kpi icon={CreditCard} label="Kredi Kartı" value={fmtTL(s.card_total)} sub="tahsilat" accent="bg-sky-600" />
              <Kpi icon={Percent} label="Verilen İndirim" value={fmtTL(s.discount_total)} sub="bu vardiya" accent="bg-red-500" />
              <Kpi icon={Wallet} label="Kasada Olması Gereken" value={fmtTL(s.expected_drawer)} sub="açılış + nakit" accent="bg-brand-orange" />
            </div>
            <p className="text-xs text-slate-400" data-testid="cashier-shift-hint">Bu kart <b className="text-slate-500">açık vardiyanızın</b> birikimli toplamıdır — bugünün geneli değil. Vardiya detayını ve kapanış (Z) raporunu Kasa ekranından alabilirsiniz.</p>
          </>
        )}
      </div>
    );
  }

  /* ---------- Yönetim paneli (config'e göre çizilir) ---------- */
  const conf = cfg || DEFAULT_DASHBOARD_CONFIG;
  const compact = conf.density === "kompakt";
  const pad = compact ? "p-3" : "p-5";
  const gap = compact ? "gap-3" : "gap-5";
  const styleRing = conf.card_style === "duz"
    ? "bg-slate-50/70 ring-1 ring-inset ring-slate-100"
    : conf.card_style === "cizgili"
      ? "bg-white ring-1 ring-inset ring-slate-200"
      : "bg-white ring-1 ring-inset ring-slate-200 shadow-sm hover:shadow-md";
  const cardCls = `rounded-xl ${styleRing} ${pad} transition-shadow`;

  const kpi = (Icon, label, value, sub, accent) => (
    <div className={`${cardCls} h-full`} data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="overline">{label}</p>
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${accent}`}><Icon className="h-4 w-4 text-white" /></div>
      </div>
      <p className="tabular mt-2 font-heading text-2xl font-bold tracking-tight text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );

  const listCard = (Icon, iconCls, title, rows, empty, onRowNav, badgeFor, showAll, setShowAll) => (
    <div className={`${cardCls} h-full`}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconCls}`} />
        <p className="font-heading text-sm font-bold text-slate-800">{title}</p>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-400">{empty}</p>}
        {(showAll ? rows : rows.slice(0, 8)).map((p) => (
          <div key={p.id} onClick={() => nav(onRowNav)} className="flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors hover:bg-slate-50">
            <img src={productImage(p)} alt="" className="h-9 w-9 rounded-md object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-800">{p.name}</p>
              <p className="font-mono text-[10px] text-slate-400">{p.sku}</p>
            </div>
            {badgeFor(p)}
          </div>
        ))}
        {rows.length > 8 && (
          <button onClick={() => setShowAll((v) => !v)} className="mt-1 flex w-full items-center justify-center gap-1 rounded-md bg-slate-50 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-100">
            {showAll ? "Daha az göster" : `Tümünü göster (${rows.length})`}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
    </div>
  );

  const R = {
    kpi_revenue_today: kpi(Wallet, "Bugün Ciro", fmtTL(d.revenue_today), `${d.sales_count_today} satış`, "bg-brand-green"),
    kpi_profit_today: kpi(TrendingUp, "Bugün Kâr", fmtTL(d.profit_today), "tahmini brüt", "bg-brand-orange"),
    kpi_open_carts: kpi(ShoppingCart, "Bekleyen Sepetler", fmtTL(d.open_carts_total || 0), `${d.open_carts_count || 0} açık · ${d.open_carts_registers || 0} kasa`, "bg-amber-500"),
    kpi_inventory_value: kpi(Boxes, "Stok Değeri", fmtTL(d.inventory_value), "maliyet bazlı", "bg-sky-600"),
    kpi_product_count: kpi(Package, "Ürün", d.total_products, `${d.in_stock_products ?? 0} stokta`, "bg-slate-800"),
    kpi_low_stock_count: kpi(AlertTriangle, "Düşük Stok", d.low_stock_count, "≤ 30 birim", "bg-red-500"),
    kpi_expiring_count: kpi(Clock, "SKT Yaklaşan", d.expiring_count, "≤ 30 gün", "bg-orange-500"),
    kpi_ai_prices: kpi(Sparkles, "AI Fiyat", d.ai_prices_today ?? 0, "gecelik otomatik", "bg-violet-600"),
    kpi_opportunities: kpi(Sparkles, "Tedarik Fırsatı", d.opportunities_count ?? 0, "daha ucuz kaynak", "bg-emerald-600"),
    kpi_market_alerts: kpi(AlertTriangle, "Piyasa Uyarısı", d.market_alerts_count ?? 0, "≥%10 sapma", "bg-amber-600"),
    kpi_avg_basket: kpi(Receipt, "Ortalama Sepet", fmtTL(d.avg_basket_today || 0), "bugün / satış", "bg-teal-600"),

    card_revenue_channels: <RevenueChannelsCard d={d} cardCls={cardCls} />,

    card_daily_summary: (() => {
      const alerts = [];
      if ((d.low_stock_count || 0) > 0) alerts.push(`${d.low_stock_count} kritik stok`);
      if ((d.expiring_count || 0) > 0) alerts.push(`${d.expiring_count} SKT yaklaşan`);
      if (d.can_purchase && (d.market_alerts_count || 0) > 0) alerts.push(`${d.market_alerts_count} piyasa uyarısı`);
      if ((d.open_carts_count || 0) > 0) alerts.push(`${d.open_carts_count} açık sepet`);
      return (
        <div className={`${cardCls} h-full`} data-testid="dash-daily-summary">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-green/10"><Sparkles className="h-4 w-4 text-brand-green" /></span>
            <div>
              <p className="font-heading text-sm font-bold text-slate-800">Bugün Ne Oldu?</p>
              <p className="text-[11px] capitalize text-slate-400">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}</p>
            </div>
          </div>
          {(d.negative_stock_count || 0) > 0 && (
            <button data-testid="dash-negative-alert" onClick={() => nav("/stok")}
              className="mb-3 flex w-full items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-left ring-1 ring-inset ring-red-200 transition-colors hover:ring-red-300">
              <AlertOctagon className="h-4 w-4 shrink-0 text-red-600" />
              <span className="text-xs font-semibold text-red-700">{d.negative_stock_count} kalemde negatif stok var — Stok › Negatif'ten düzeltin</span>
              <span className="ml-auto text-[11px] font-semibold text-red-600">Git →</span>
            </button>
          )}
          <div className="grid gap-2 sm:grid-cols-4">
            <button data-testid="daily-sum-sales" onClick={() => nav("/satislar")} className="rounded-lg bg-emerald-50 p-3 text-left ring-1 ring-inset ring-emerald-100 transition-colors hover:ring-emerald-300">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700"><Receipt className="h-3.5 w-3.5" /> Satış / Ciro</div>
              <p className="tabular mt-1 font-heading text-lg font-bold text-emerald-800">{d.sales_count_today || 0} · {fmtTL(d.revenue_today || 0)}</p>
            </button>
            <button data-testid="daily-sum-basket" onClick={() => nav("/raporlar")} className="rounded-lg bg-slate-50 p-3 text-left ring-1 ring-inset ring-slate-100 transition-colors hover:ring-slate-300">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><TrendingUp className="h-3.5 w-3.5" /> Ortalama Sepet</div>
              <p className="tabular mt-1 font-heading text-lg font-bold text-slate-800">{fmtTL(d.avg_basket_today || 0)}</p>
            </button>
            <button data-testid="daily-sum-low" onClick={() => nav("/mal-kabul")} className={`rounded-lg p-3 text-left ring-1 ring-inset transition-colors ${(d.low_stock_count || 0) > 0 ? "bg-red-50 ring-red-100 hover:ring-red-300" : "bg-slate-50 ring-slate-100"}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> Kritik Stok</div>
              <p className="tabular mt-1 font-heading text-lg font-bold text-slate-800">{d.low_stock_count || 0}</p>
            </button>
            <button data-testid="daily-sum-exp" onClick={() => nav("/stok?filter=expiring")} className={`rounded-lg p-3 text-left ring-1 ring-inset transition-colors ${(d.expiring_count || 0) > 0 ? "bg-orange-50 ring-orange-100 hover:ring-orange-300" : "bg-slate-50 ring-slate-100"}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-orange-600"><Clock className="h-3.5 w-3.5" /> SKT Yaklaşan</div>
              <p className="tabular mt-1 font-heading text-lg font-bold text-slate-800">{d.expiring_count || 0}</p>
            </button>
          </div>
          <p className="mt-2.5 text-[13px] leading-relaxed text-slate-600" data-testid="daily-summary-text">
            {alerts.length > 0 ? <>Dikkat: <b>{alerts.join(" · ")}</b>.</> : <>Her şey yolunda — kritik uyarı yok. İyi satışlar!</>}
            {d.top_customer_today ? <> Bugünün en iyi müşterisi <b>{d.top_customer_today.name}</b> ({fmtTL(d.top_customer_today.total)}).</> : null}
          </p>
        </div>
      );
    })(),

    card_top_customer: (
      <div className={`${cardCls} h-full`} data-testid="dash-top-customer">
        <div className="mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-brand-green" /><p className="font-heading text-sm font-bold text-slate-800">Bugünün En Çok Kazandıran Müşterisi</p></div>
        {d.top_customer_today ? (
          <div data-testid="top-customer-open"
            onClick={() => d.top_customer_today.id && openCustomerHistory(d.top_customer_today.id, d.top_customer_today.name)}
            className={`flex items-center gap-3 rounded-lg ${d.top_customer_today.id ? "cursor-pointer p-1.5 transition-colors hover:bg-slate-50" : ""}`}>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-green/10 text-lg font-bold text-brand-green">{(d.top_customer_today.name || "?").slice(0, 1).toUpperCase()}</div>
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-slate-800">{d.top_customer_today.name}</p><p className="text-[11px] text-slate-500">{d.top_customer_today.count} satış{d.top_customer_today.id ? " · detay için tıkla" : ""}</p></div>
            <p className="tabular shrink-0 font-heading text-xl font-bold text-brand-green">{fmtTL(d.top_customer_today.total)}</p>
          </div>
        ) : <p className="py-4 text-center text-sm text-slate-400">Bugün müşteri kayıtlı satış yok.</p>}
      </div>
    ),

    card_top_brands: <TopBrandsCard cardCls={cardCls} nav={nav} />,

    card_shift_reco: (
      <div className={`${cardCls} h-full`} data-testid="dash-shift-reco">
        <div className="mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /><p className="font-heading text-sm font-bold text-slate-800">AI Vardiya Önerisi</p></div>
        {!shiftReco ? (
          <p className="py-4 text-center text-sm text-slate-400">Öneri hazırlanıyor…</p>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-slate-600" data-testid="shift-reco-note">{shiftReco.note}</p>
            {shiftReco.busy_hours && shiftReco.busy_hours.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="shift-reco-hours">
                {shiftReco.busy_hours.map((b) => (
                  <span key={b.hour} className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
                    <Clock className="h-3 w-3" /> {String(b.hour).padStart(2, "0")}:00
                  </span>
                ))}
              </div>
            )}
            {!shiftReco.insufficient && (
              <Button data-testid="shift-plan-generate" size="sm" onClick={openShiftSchedule} disabled={shiftPlan.loading}
                className="mt-3 w-full bg-violet-600 text-white hover:bg-violet-700">
                <CalendarDays className="mr-1.5 h-4 w-4" /> Haftalık Plana Çevir
              </Button>
            )}
          </>
        )}
      </div>
    ),

    card_ai_images: (
      <div className={`${cardCls} h-full`} data-testid="dash-ai-images">
        <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-fuchsia-600" /><p className="font-heading text-sm font-bold text-slate-800">AI Toplu Görsel Üretimi</p></div>
        {aiImg == null ? (
          <p className="py-3 text-center text-sm text-slate-400">Durum yükleniyor…</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm ring-1 ring-inset ring-slate-200">
              <span className="text-slate-500">Görselsiz ürün</span>
              <span className="tabular font-bold text-slate-800" data-testid="ai-images-missing">{aiImg.missing_total ?? 0}</span>
            </div>
            {aiImg.running ? (
              <div data-testid="ai-images-progress">
                <div className="mb-1 flex justify-between text-[11px] text-slate-500"><span>Üretiliyor… {aiImg.done ?? 0}/{aiImg.total ?? 0}</span>{aiImg.failed ? <span className="text-red-500">{aiImg.failed} hata</span> : null}</div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-fuchsia-500 transition-all" style={{ width: `${aiImg.total ? Math.round(((aiImg.done || 0) / aiImg.total) * 100) : 0}%` }} /></div>
              </div>
            ) : (
              <>
                {aiImg.stop_reason === "llm_limit" && <p className="rounded-md bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">Son denemede günlük AI limiti dolmuştu. Limit sıfırlanınca tekrar deneyin.</p>}
                <Button data-testid="ai-images-trigger" onClick={triggerAiImages} disabled={aiImgBusy || (aiImg.missing_total ?? 0) === 0} className="w-full bg-fuchsia-600 text-white hover:bg-fuchsia-700">
                  <Sparkles className="mr-1.5 h-4 w-4" /> {(aiImg.missing_total ?? 0) === 0 ? "Tüm ürünlerde görsel var" : "Görsel Üret (otomatik devam)"}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    ),

    chart_revenue_7d: (
      <div className={`${cardCls} h-full`}>
        <p className="overline mb-3">Son 7 Gün Ciro</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={d.revenue_trend}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#064E3B" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#064E3B" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" width={40} />
            <Tooltip formatter={(v) => fmtTL(v)} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} />
            <Area type="monotone" dataKey="revenue" stroke="#064E3B" strokeWidth={2} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    ),

    chart_category_dist: (
      <div className={`${cardCls} h-full`}>
        <p className="overline mb-3">Kategori Dağılımı</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={d.category_distribution} layout="vertical" margin={{ left: 10 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="category" width={90} tick={{ fontSize: 9 }} stroke="#94a3b8" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} />
            <Bar dataKey="count" fill="#EA580C" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ),

    chart_payment_mix: (() => {
      const pm = d.payment_mix || { cash: 0, card: 0, veresiye: 0 };
      const data = [{ name: "Nakit", value: pm.cash, color: "#059669" }, { name: "Kart", value: pm.card, color: "#0284c7" }, { name: "Veresiye", value: pm.veresiye, color: "#f59e0b" }].filter((x) => (x.value || 0) > 0);
      const tot = data.reduce((a, b) => a + b.value, 0);
      return (
        <div className={`${cardCls} h-full`} data-testid="dash-payment-mix">
          <p className="overline mb-2">Ödeme Yöntemi Dağılımı (30 gün)</p>
          {tot > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend formatter={(val, entry) => `${val} · %${Math.round(((entry.payload.value || 0) / tot) * 100)}`} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="py-10 text-center text-sm text-slate-400">Son 30 günde ödeme verisi yok.</p>}
        </div>
      );
    })(),

    chart_weekly_profit: (() => {
      const wp = (d.weekly_profit || []).map((w) => ({ ...w, label: (() => { try { return new Date(w.week).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }); } catch { return (w.week || "").slice(5); } })() }));
      const has = wp.some((w) => (w.profit || 0) !== 0);
      return (
        <div className={`${cardCls} h-full`} data-testid="dash-weekly-profit">
          <p className="overline mb-2">Haftalık Kâr Trendi (8 hafta)</p>
          {has ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={wp}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" width={44} />
                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="profit" fill="#064E3B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="py-10 text-center text-sm text-slate-400">Son 8 haftada kâr verisi yok.</p>}
        </div>
      );
    })(),

    chart_hourly_heatmap: (() => {
      const hm = d.sales_heatmap || { days: [], matrix: [], max: 0 };
      const wd = (ds) => { try { return new Date(ds).toLocaleDateString("tr-TR", { weekday: "short" }); } catch { return (ds || "").slice(5); } };
      return (
        <div className={`${cardCls} h-full`} data-testid="dash-hourly-heatmap">
          <div className="mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-brand-orange" /><p className="font-heading text-sm font-bold text-slate-800">Saatlik Satış Isı Haritası (7 gün)</p></div>
          {hm.max > 0 ? (
            <div className="overflow-x-auto">
              <div className="min-w-[560px]">
                <div className="flex">
                  <div className="w-10 shrink-0" />
                  {Array.from({ length: 24 }).map((_, h) => <div key={h} className="flex-1 text-center text-[8px] text-slate-400">{h % 3 === 0 ? h : ""}</div>)}
                </div>
                {hm.matrix.map((row, ri) => (
                  <div key={ri} className="flex items-center">
                    <div className="w-10 shrink-0 pr-1 text-right text-[9px] font-semibold text-slate-500">{wd(hm.days[ri])}</div>
                    {row.map((v, h) => { const it = hm.max > 0 ? v / hm.max : 0; return <div key={h} data-testid={`heatmap-cell-${ri}-${h}`} onClick={() => openHourDetail(hm.days[ri], h)} className="mx-[1px] my-[1px] h-4 flex-1 cursor-pointer rounded-sm transition-shadow hover:ring-2 hover:ring-brand-green/50" title={`${wd(hm.days[ri])} ${h}:00 · ${fmtTL(v)} (detay için tıkla)`} style={{ background: v > 0 ? `rgba(6,78,59,${(0.12 + it * 0.88).toFixed(2)})` : "#f1f5f9" }} />; })}
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="py-6 text-center text-sm text-slate-400">Son 7 günde saatlik satış verisi yok.</p>}
          <p className="mt-2 text-[10px] text-slate-400">Koyu = yüksek ciro. En yoğun saatlerinizi gösterir.</p>
        </div>
      );
    })(),

    progress_catalog: ch ? (
      <div data-testid="dash-catalog-readiness" onClick={() => nav("/katalog-sagligi")}
        className={`${cardCls} h-full cursor-pointer hover:ring-brand-green/40`}>
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-brand-green" />
          <p className="font-heading text-sm font-bold text-slate-800">Katalog Hazırlık</p>
          <span className="ml-auto text-[11px] font-semibold text-brand-green">Katalog Sağlığı →</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="mb-1 flex justify-between text-xs"><span className="text-slate-500">Fiyatlanan ürün</span><span className="tabular font-semibold text-slate-700">{ch.priced}/{ch.total} (%{ch.priced_pct})</span></div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand-green transition-all" style={{ width: `${ch.priced_pct}%` }} /></div>
          </div>
          <div>
            <div className="mb-1 flex justify-between text-xs"><span className="text-slate-500">KDV doğrulanan</span><span className="tabular font-semibold text-slate-700">{ch.kdv.ok}/{ch.total} (%{ch.kdv.verified_pct})</span></div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${ch.kdv.verified_pct}%` }} /></div>
          </div>
        </div>
      </div>
    ) : null,

    card_expiry_alert: (expAlert && (expAlert.expiring_7 + expAlert.quarantine + expAlert.expired) > 0) ? (
      <div data-testid="expiry-alert-card" onClick={() => nav("/stok?filter=expiring")}
        className={`${cardCls} h-full cursor-pointer ring-amber-200 hover:ring-amber-300`}>
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <p className="font-heading text-sm font-bold text-slate-800">SKT &amp; Karantina Uyarısı</p>
          <span className="ml-auto text-[11px] font-semibold text-amber-600">Stok'a git →</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MiniStat testid="exp-7" label="7 gün içinde SKT" value={expAlert.expiring_7} tone="red" />
          <MiniStat testid="exp-30" label="30 gün içinde SKT" value={expAlert.expiring_30} tone="orange" />
          <MiniStat testid="exp-expired" label="Süresi geçmiş lot" value={expAlert.expired} tone="rose" />
          <MiniStat testid="exp-quar" label="Karantina lot" value={expAlert.quarantine} tone="slate" />
        </div>
      </div>
    ) : null,

    card_vehicle_docs: (vehDocs && vehDocs.length > 0) ? (
      <div data-testid="dash-vehicle-docs" onClick={() => nav("/lojistik")}
        className={`${cardCls} h-full cursor-pointer ring-amber-200 hover:ring-amber-300`}>
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <p className="font-heading text-sm font-bold text-slate-800">Belgesi Yakında Dolan Araçlar</p>
          <span className="ml-auto text-[11px] font-semibold text-amber-600">Lojistik'e git →</span>
        </div>
        <div className="max-h-56 space-y-1.5 overflow-y-auto">
          {vehDocs.map((v) => (
            <div key={v.id} data-testid={`dash-vehicle-doc-${v.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs ring-1 ring-inset ring-slate-200">
              <span className="font-semibold text-slate-700">{v.plate}{v.driver?.name ? ` · ${v.driver.name}` : ""}</span>
              <span className="flex flex-wrap justify-end gap-1">
                {(v.doc_alerts || []).map((a, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.status === "expired" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    {a.label}: {a.status === "expired" ? "süresi geçti" : `${a.days_left} gün`}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null,

    grid_integration_health: (health && health.items && health.items.length > 0) ? (
      <div className={`${cardCls} h-full`} data-testid="integration-health-card">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Plug className="h-4 w-4 text-brand-green" />
            <p className="font-heading text-sm font-bold text-slate-800">Entegrasyon Sağlığı</p>
          </div>
          <span data-testid="integration-synced-24h" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <RefreshCw className="h-3 w-3" /> Son 24s: {health.synced_24h ?? 0} ürün
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-2">
          {health.items.map((h) => {
            const st = h.configured
              ? (h.enabled ? { t: "Bağlı", c: "text-emerald-600", d: "bg-emerald-500" } : { t: "Pasif", c: "text-amber-600", d: "bg-amber-500" })
              : { t: "Yapılandırılmadı", c: "text-slate-400", d: "bg-slate-300" };
            return (
              <div key={h.key} data-testid={`integration-health-${h.key}`} className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-semibold text-slate-700">{h.label}</span>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${st.d}`} />
                </div>
                <div className={`mt-1 text-sm font-bold ${st.c}`}>{st.t}</div>
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {h.key === "marketplace" && h.count != null ? `${h.active}/${h.count} aktif · ` : ""}
                  {h.last_sync ? `son: ${new Date(h.last_sync).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : "senkron kaydı yok"}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(h.key === "ticimax" || h.key === "trendyol") && h.configured && (
                    <button data-testid={`integration-sync-${h.key}`} onClick={(e) => { e.stopPropagation(); syncIntegration(h.key); }} disabled={syncing === h.key}
                      className="inline-flex items-center gap-1 rounded bg-brand-green px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-green-600 disabled:opacity-50">
                      <RefreshCw className={`h-3 w-3 ${syncing === h.key ? "animate-spin" : ""}`} /> Senkronla
                    </button>
                  )}
                  <button data-testid={`integration-config-${h.key}`} onClick={() => openIntegration(h.key)}
                    className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-100">
                    <Settings2 className="h-3 w-3" /> Ayarla
                  </button>
                  {h.key !== "edoc" && (
                    <button data-testid={`integration-history-${h.key}`} onClick={() => openHistory(h)}
                      className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-100">
                      <History className="h-3 w-3" /> Geçmiş
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ) : null,

    card_ai_insights: <div className="h-full"><AiInsightsCard /></div>,

    table_cash_location: (d.payments_by_location && d.payments_by_location.length > 0) ? (
      <div className={`${cardCls} h-full`} data-testid="dashboard-cash-by-location">
        <div className="mb-3 flex items-center gap-2">
          <Banknote className="h-4 w-4 text-emerald-600" />
          <p className="font-heading text-sm font-bold text-slate-800">Bugün Kasa — Mağaza/Depo Bazında (Nakit / Kart)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2 font-medium">Lokasyon</th><th className="px-3 py-2 text-right font-medium">Nakit</th><th className="px-3 py-2 text-right font-medium">Kart</th><th className="px-3 py-2 text-right font-medium">Açık/Fazla</th><th className="px-3 py-2 text-right font-medium">Ciro</th><th className="px-3 py-2 text-right font-medium">Satış</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.payments_by_location.map((l) => (
                <tr key={l.location_id} data-testid={`cash-loc-${l.location_id}`} className="transition-colors hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-700"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />{l.name}</span></td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-emerald-700">{fmtTL(l.cash)}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-sky-700">{fmtTL(l.card)}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold">{l.over_short == null ? <span className="text-slate-300">—</span> : <span className={l.over_short < 0 ? "text-red-600" : l.over_short > 0 ? "text-emerald-700" : "text-slate-400"}>{l.over_short < 0 ? "−" : l.over_short > 0 ? "+" : ""}{fmtTL(Math.abs(l.over_short))}</span>}</td>
                  <td className="px-3 py-2 text-right tabular text-slate-600">{fmtTL(l.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular text-slate-400">{l.count}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 font-bold">
                <td className="px-3 py-2 text-slate-700">Toplam</td>
                <td className="px-3 py-2 text-right tabular text-emerald-700">{fmtTL(d.cash_today)}</td>
                <td className="px-3 py-2 text-right tabular text-sky-700">{fmtTL(d.card_today)}</td>
                <td className={`px-3 py-2 text-right tabular ${d.over_short_today < 0 ? "text-red-600" : d.over_short_today > 0 ? "text-emerald-700" : "text-slate-400"}`}>{d.over_short_counted ? `${d.over_short_today < 0 ? "−" : d.over_short_today > 0 ? "+" : ""}${fmtTL(Math.abs(d.over_short_today))}` : "—"}</td>
                <td className="px-3 py-2 text-right tabular text-slate-700">{fmtTL(d.revenue_today)}</td>
                <td className="px-3 py-2 text-right tabular text-slate-400">{d.sales_count_today}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    ) : null,

    list_sales_analysis: (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="dashboard-sales-analytics">
        <SalesRankCard testid="sales-rank-top" title="En Çok Satan (30g)" icon={TrendingUp} accent="text-emerald-600" rows={d.top_sellers} metric="qty" nav={nav} cardCls={`${cardCls} h-full`} />
        {d.can_purchase && d.most_profitable
          ? <SalesRankCard testid="sales-rank-profit" title="En Karlı (30g)" icon={Award} accent="text-amber-600" rows={d.most_profitable} metric="profit" nav={nav} cardCls={`${cardCls} h-full`} />
          : <SalesRankCard testid="sales-rank-revenue" title="En Çok Ciro (30g)" icon={Receipt} accent="text-sky-600" rows={d.top_sellers} metric="qty" nav={nav} cardCls={`${cardCls} h-full`} />}
        <SalesRankCard testid="sales-rank-worst" title="En Az Satan (stokta)" icon={TrendingDown} accent="text-slate-500" rows={d.worst_sellers} metric="qty" nav={nav} cardCls={`${cardCls} h-full`} />
      </div>
    ),

    list_low_stock: listCard(AlertTriangle, "text-red-500", "Düşük Stok Uyarısı", d.low_stock_list, "Stok seviyeleri iyi.", "/mal-kabul", (p) => <StockBadge qty={p.stock_on_hand} />, showAllLow, setShowAllLow),
    list_expiring: listCard(Clock, "text-orange-500", "SKT Yaklaşan Ürünler", d.expiring_list, "Yaklaşan SKT yok.", "/urunler", (p) => <ExpiryBadge dateStr={p.expiry_date} />, showAllExp, setShowAllExp),

    list_reorder: <div className="h-full"><ReorderCard /></div>,

    cards_supply_ops: (d.opportunities_list && d.opportunities_list.length > 0) ? (
      <div className={`${cardCls} h-full`}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <p className="font-heading text-sm font-bold text-slate-800">Tedarik Fırsatları — daha ucuza alabilirsin</p>
          </div>
          <button data-testid="see-all-opportunities" onClick={() => nav("/firsatlar")} className="text-xs font-semibold text-brand-green hover:underline">Tümünü gör</button>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {d.opportunities_list.slice(0, 6).map((p) => (
            <div key={p.id} data-testid={`dash-opp-${p.id}`} onClick={() => nav("/firsatlar")}
              className="flex cursor-pointer items-center gap-3 rounded-md bg-emerald-50 p-2.5 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100">
              <img src={productImage(p)} alt="" className="h-10 w-10 rounded-md object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800">{p.name}</p>
                <p className="text-[11px] text-emerald-700"><b>{p.opportunity.site}</b>: {fmtTL(p.opportunity.price)} <span className="text-slate-400">(alış {fmtTL(p.opportunity.our_cost)})</span></p>
              </div>
              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-1 text-xs font-bold text-white tabular">
                <TrendingDown className="h-3.5 w-3.5" /> %{p.opportunity.saving_pct}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null,
  };

  const visible = resolveVisibleWidgets(conf);

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="overline">Groot Market · Panel</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Merhaba {user?.name?.split(" ")[0]}</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="dashboard-purpose">Günlük canlı operasyon — bugünkü kasa, stok/SKT uyarıları ve fırsatlar. Panelini <b>Ayarlar → Tasarım → Panel Tasarımı</b>'ndan özelleştirebilirsin.</p>
        </div>
        <div className="flex items-center gap-2">
          {d.location_scope && d.location_scope.length > 0 && (
            <div data-testid="dashboard-scope" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <MapPin className="h-3.5 w-3.5" /> {d.location_scope.map((l) => l.name).join(", ")} verileri
            </div>
          )}
        </div>
      </div>

      {dueSum?.due && ((dueSum.due.overdue || 0) + (dueSum.due.upcoming || 0)) > 0 && (
        <button data-testid="dash-due-reminder" onClick={() => nav("/muhasebe")}
          className="flex w-full flex-wrap items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 text-left ring-1 ring-inset ring-amber-200 transition-colors hover:ring-amber-300">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100"><Clock className="h-5 w-5 text-amber-700" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-amber-800">Çek / Senet Vade Hatırlatması</p>
            <p className="text-xs text-amber-700">
              {(dueSum.due.overdue || 0) > 0 && <span className="font-semibold text-red-600">{dueSum.due.overdue} gecikmiş</span>}
              {(dueSum.due.overdue || 0) > 0 && (dueSum.due.upcoming || 0) > 0 && " · "}
              {(dueSum.due.upcoming || 0) > 0 && <span>{dueSum.due.upcoming} yaklaşan ({dueSum.due.window_days} gün içinde)</span>}
              {" — Ödenecek "}<b>{fmtTL(dueSum.due.out_total || 0)}</b>{" · Tahsil "}<b>{fmtTL(dueSum.due.in_total || 0)}</b>
            </p>
          </div>
          <span className="shrink-0 text-xs font-semibold text-amber-700">Vadeler →</span>
        </button>
      )}

      {docExp && (docExp.items || []).length > 0 && (
        <div data-testid="dash-doc-expiry" className="rounded-xl bg-rose-50 px-4 py-3 ring-1 ring-inset ring-rose-200">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-100"><AlertTriangle className="h-5 w-5 text-rose-700" /></span>
            <div>
              <p className="text-sm font-bold text-rose-800">Evrak Vade Uyarısı</p>
              <p className="text-xs text-rose-700">{docExp.expired || 0} süresi geçmiş · {docExp.upcoming || 0} yaklaşan (30 gün içinde) — sözleşme, sigorta, ruhsat vb.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {docExp.items.slice(0, 6).map((it) => (
              <span key={it.doc_id} data-testid={`doc-expiry-${it.doc_id}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${it.status === "expired" ? "bg-rose-200 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                {it.entity_label}: {it.entity_name} · {it.doc_name} {it.status === "expired" ? `(${-it.days_left}g geçti)` : `(${it.days_left}g kaldı)`}
              </span>
            ))}
            {docExp.items.length > 6 && <span className="text-[11px] font-semibold text-rose-600">+{docExp.items.length - 6} daha</span>}
          </div>
        </div>
      )}

      {docComp && (docComp.items || []).length > 0 && (
        <button data-testid="dash-doc-compliance" onClick={() => nav("/belge-arsivi")}
          className="flex w-full flex-col gap-1.5 rounded-xl bg-indigo-50 px-4 py-3 text-left ring-1 ring-inset ring-indigo-200 transition-colors hover:ring-indigo-300">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100"><FileText className="h-5 w-5 text-indigo-700" /></span>
            <div>
              <p className="text-sm font-bold text-indigo-800">Eksik Zorunlu Evrak Uyarısı</p>
              <p className="text-xs text-indigo-700">{docComp.count} varlıkta {docComp.missing_total} zorunlu evrak eksik — sözleşmesi olmayan personel, ruhsatsız araç vb.</p>
            </div>
            <span className="ml-auto shrink-0 text-xs font-semibold text-indigo-700">Belge Arşivi →</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {docComp.items.slice(0, 6).map((it) => (
              <span key={it.entity_id} data-testid={`doc-compliance-${it.entity_id}`} className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-800">
                {it.entity_label}: {it.entity_name} · eksik: {it.missing.map((mm) => mm.label).join(", ")}
              </span>
            ))}
            {docComp.items.length > 6 && <span className="text-[11px] font-semibold text-indigo-600">+{docComp.items.length - 6} daha</span>}
          </div>
        </button>
      )}

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="dashboard-empty">
          <SlidersHorizontal className="mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-semibold text-slate-600">Panelde gösterilecek bileşen seçilmemiş.</p>
          <p className="mt-1 text-xs text-slate-400">Ayarlar → Tasarım → Panel Tasarımı'ndan bir düzen seçin veya bileşen açın.</p>
          <Button data-testid="dashboard-empty-cta" onClick={() => nav("/ayarlar?tab=design")} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">Panel Tasarımı'na Git</Button>
        </div>
      ) : (
        <div className={`grid grid-cols-2 lg:grid-cols-4 ${gap}`} data-testid="dashboard-grid">
          {visible.map((key) => {
            const w = WIDGET_CATALOG[key];
            if (!w) return null;
            if (w.perm === "can_purchase" && !d.can_purchase) return null;
            if (w.perm === "is_admin" && !d.is_admin) return null;
            if (w.perm === "logistics" && !can("logistics.read")) return null;
            const node = R[key];
            if (node == null) return null;
            return <div key={key} className={SPAN_CLS[w.span] || "col-span-1"} data-testid={`dash-w-${key}`}>{node}</div>;
          })}
        </div>
      )}

      <Dialog open={hist.open} onOpenChange={(o) => setHist((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="sync-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4 text-brand-green" /> Senkron Geçmişi · {hist.label}</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {hist.loading ? (
              <p className="py-6 text-center text-sm text-slate-400">Yükleniyor…</p>
            ) : hist.items && hist.items.length > 0 ? (
              <ul className="divide-y divide-slate-100">
                {hist.items.map((it) => (
                  <li key={it.id} data-testid="sync-history-row" className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-700">{new Date(it.ts).toLocaleString("tr-TR")}</div>
                      {it.name && <div className="truncate text-[11px] text-slate-400">{it.name}</div>}
                      {it.error && <div className="truncate text-[11px] text-red-500">{it.error}</div>}
                    </div>
                    <div className="shrink-0 text-right">
                      {it.ok ? (
                        <span className="text-xs font-semibold text-emerald-600">{it.sent} gönderildi{it.skipped ? ` · ${it.skipped} atlandı` : ""}</span>
                      ) : (
                        <span className="text-xs font-semibold text-red-500">Hata</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-6 text-center text-sm text-slate-400">Henüz senkron kaydı yok.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={hourDetail.open} onOpenChange={(o) => setHourDetail((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="hour-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4 text-brand-orange" /> Saatlik Satış · {hourDetail.hour != null ? `${hourDetail.hour}:00–${hourDetail.hour}:59` : ""}</DialogTitle>
          </DialogHeader>
          {hourDetail.loading ? (
            <p className="py-6 text-center text-sm text-slate-400">Yükleniyor…</p>
          ) : hourDetail.data && hourDetail.data.count > 0 ? (
            <div>
              <div className="mb-2 flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm ring-1 ring-inset ring-slate-200">
                <span className="text-slate-500">{hourDetail.data.count} satış</span>
                <span className="tabular font-bold text-brand-green">{fmtTL(hourDetail.data.total)}</span>
              </div>
              <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
                {(hourDetail.data.items || []).map((it, i) => (
                  <li key={i} data-testid="hour-detail-row" className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-slate-700">{it.time} · {it.sale_no}</div>
                      <div className="truncate text-[11px] text-slate-400">{it.item_count} kalem{it.customer_name ? ` · ${it.customer_name}` : ""}{it.location_name ? ` · ${it.location_name}` : ""}</div>
                    </div>
                    <span className="tabular shrink-0 text-sm font-semibold text-slate-700">{fmtTL(it.total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : <p className="py-6 text-center text-sm text-slate-400">Bu saatte satış yok.</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={custHist.open} onOpenChange={(o) => setCustHist((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="customer-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4 text-brand-green" /> {custHist.name || "Müşteri"} · Son 30 Gün</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">Müşterinin son 30 günlük harcaması, en çok aldığı ürünler ve iletişim kısayolları.</DialogDescription>
          </DialogHeader>
          {custHist.loading ? (
            <p className="py-6 text-center text-sm text-slate-400">Yükleniyor…</p>
          ) : custHist.data ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-slate-50 p-3 text-center ring-1 ring-inset ring-slate-200">
                  <p className="tabular font-heading text-lg font-bold text-brand-green" data-testid="cust-hist-total">{fmtTL(custHist.data.total30 || 0)}</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">30 gün harcama</p>
                </div>
                <div className="rounded-md bg-slate-50 p-3 text-center ring-1 ring-inset ring-slate-200">
                  <p className="tabular font-heading text-lg font-bold text-slate-800">{(custHist.data.sales30 || []).length}</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">satış (30 gün)</p>
                </div>
                <div className="rounded-md bg-slate-50 p-3 text-center ring-1 ring-inset ring-slate-200">
                  <p className="tabular font-heading text-lg font-bold text-slate-800">{fmtTL(custHist.data.totals?.total_revenue || 0)}</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">tüm zaman</p>
                </div>
              </div>

              {(custHist.data.monthly_spend || []).some((m) => m.total > 0) && (
                <div data-testid="cust-monthly-chart">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Son 6 Ay Harcama</p>
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={custHist.data.monthly_spend} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                      <XAxis dataKey="month" tickFormatter={(v) => (v || "").slice(5)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                      <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="total" fill="#0f7a3d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {(custHist.data.top_products || []).length > 0 && (
                <div data-testid="cust-top-products">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">En Çok Aldığı Ürünler</p>
                  <div className="space-y-1">
                    {custHist.data.top_products.map((p, i) => (
                      <div key={i} data-testid="cust-top-product-row" className="flex items-center gap-2 text-sm">
                        <span className="w-4 shrink-0 text-center text-[11px] font-bold text-slate-400">{i + 1}</span>
                        <span className="min-w-0 flex-1 truncate text-slate-700">{p.name}</span>
                        <span className="shrink-0 text-[11px] text-slate-400">{p.qty} ad</span>
                        <span className="tabular shrink-0 text-xs font-semibold text-slate-700">{fmtTL(p.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Hızlı İletişim</p>
                  {!custHist.data.customer?.phone && <span className="text-[11px] text-amber-600">Telefon kayıtlı değil</span>}
                </div>
                <Textarea data-testid="cust-sms-text" value={smsBox.text} onChange={(e) => setSmsBox((s) => ({ ...s, text: e.target.value }))}
                  placeholder="Müşteriye gönderilecek SMS mesajı…" rows={2} className="mb-2 resize-none bg-white text-sm" />
                <div className="flex flex-wrap items-center gap-2">
                  <Button data-testid="cust-discount-btn" size="sm" variant="outline" onClick={applyDiscountTemplate}>
                    <Tag className="mr-1 h-3.5 w-3.5" /> İndirim Mesajı Hazırla
                  </Button>
                  <Button data-testid="cust-sms-send" size="sm"
                    disabled={smsBox.sending || !custHist.data.customer?.phone || smsBox.text.trim().length < 3}
                    onClick={() => sendCustomerSms(custHist.data.customer?.id, custHist.data.customer?.name)}
                    className="bg-brand-green text-white hover:bg-brand-green/90">
                    <Send className="mr-1 h-3.5 w-3.5" /> SMS Gönder
                  </Button>
                </div>
              </div>

              {(custHist.data.sales30 || []).length > 0 ? (
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Son 30 Gün Satışları</p>
                  <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
                    {custHist.data.sales30.map((s, i) => (
                      <li key={i} data-testid="cust-hist-row" className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-slate-700">{s.sale_no || "—"}</div>
                          <div className="truncate text-[11px] text-slate-400">{new Date(s.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {s.items} kalem{s.discount ? ` · indirim ${fmtTL(s.discount)}` : ""}</div>
                        </div>
                        <span className="tabular shrink-0 text-sm font-semibold text-slate-700">{fmtTL(s.total)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : <p className="py-3 text-center text-sm text-slate-400">Son 30 günde satış yok.</p>}
            </div>
          ) : <p className="py-6 text-center text-sm text-slate-400">Müşteri geçmişi yüklenemedi.</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={shiftPlan.open} onOpenChange={(o) => setShiftPlan((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="shift-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><CalendarDays className="h-4 w-4 text-violet-600" /> Haftalık Vardiya Çizelgesi</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">Satış yoğunluğu önerisine göre her güne gerçek personel atayın; çizelge hafta hafta kaydedilir.</DialogDescription>
          </DialogHeader>
          {shiftPlan.loading ? (
            <p className="py-6 text-center text-sm text-slate-400">Yükleniyor…</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-2 py-1.5 ring-1 ring-inset ring-slate-200">
                <Button data-testid="shift-week-prev" size="sm" variant="ghost" onClick={() => changeWeek(-1)}><ChevronDown className="h-4 w-4 rotate-90" /></Button>
                <span className="text-sm font-bold text-slate-700" data-testid="shift-week-label">{shiftPlan.weekStart} haftası</span>
                <Button data-testid="shift-week-next" size="sm" variant="ghost" onClick={() => changeWeek(1)}><ChevronDown className="h-4 w-4 -rotate-90" /></Button>
              </div>
              {shiftPlan.personnel.length === 0 && <p className="rounded-md bg-amber-50 px-3 py-2 text-[12px] text-amber-700 ring-1 ring-inset ring-amber-200">Personel bulunamadı — Muhasebe → Personel'den ekleyin.</p>}
              <div className="space-y-2">
                {shiftPlan.days.map((dd, i) => (
                  <div key={i} data-testid={`shift-plan-day-${i}`} className="rounded-lg border border-slate-200 p-3">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-slate-800">{dd.weekday} <span className="text-[11px] font-normal text-slate-400">{(dd.date || "").slice(5)}</span></p>
                      {dd.blocks && dd.blocks.length > 0 && (
                        <div className="flex flex-wrap justify-end gap-1">
                          {dd.blocks.map((b, j) => (
                            <span key={j} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${b.level === "Yoğun" ? "bg-red-50 text-red-700 ring-red-200" : b.level === "Orta" ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}>
                              {String(b.start).padStart(2, "0")}-{String(b.end).padStart(2, "0")} · {b.staff}k
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {shiftPlan.personnel.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {shiftPlan.personnel.map((p) => {
                          const on = dd.assigned_ids.includes(p.id);
                          return (
                            <button key={p.id} type="button" data-testid={`shift-assign-${i}-${p.id}`} onClick={() => toggleAssign(i, p.id)}
                              className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${on ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                              {on ? "✓ " : ""}{p.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <input data-testid={`shift-note-${i}`} value={dd.note} onChange={(e) => setDayNote(i, e.target.value)} placeholder="Not (örn. sabah / akşam vardiyası)"
                      className="mt-2 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-violet-300" />
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="shift-schedule-save" onClick={saveSchedule} disabled={shiftPlan.saving} className="flex-1 bg-violet-600 text-white hover:bg-violet-700"><Save className="mr-1.5 h-4 w-4" /> Çizelgeyi Kaydet</Button>
                <Button data-testid="shift-plan-share" onClick={shareSchedule} disabled={shiftPlan.sharing} variant="outline" className="flex-1"><Share2 className="mr-1.5 h-4 w-4" /> Kaydet & Ekiple Paylaş</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-inset ring-slate-200 shadow-sm" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="overline">{label}</p>
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${accent}`}><Icon className="h-4 w-4 text-white" /></div>
      </div>
      <p className="tabular mt-2 font-heading text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}

function MiniStat({ label, value, tone, testid }) {
  const tones = { red: "text-red-600", orange: "text-orange-600", rose: "text-rose-600", slate: "text-slate-700" };
  return (
    <div className="rounded-md bg-slate-50 p-3 text-center ring-1 ring-inset ring-slate-200" data-testid={testid}>
      <p className={`tabular font-heading text-2xl font-bold ${tones[tone] || "text-slate-700"}`}>{value ?? 0}</p>
      <p className="mt-0.5 text-[11px] text-slate-500">{label}</p>
    </div>
  );
}
